/**
 * Parse bank-statement lines copied from online banking (#2897).
 *
 * Column order is not assumed: each token is classified as an amount, a
 * Finnish / RF payment reference, a date, or leftover payer-name text.
 * Amounts become integer cents via eurosToCents.
 */

import { eurosToCents } from '@/utils/money';

export interface ParsedStatementRow {
  amountCents: number;
  reference: string | null;
  payerName: string | null;
  /** Calendar date YYYY-MM-DD when the line carried a date. */
  paymentDate: string | null;
}

const DATE_DMY_DOT = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
const DATE_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_DMY_SLASH = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

function pad2(value: string): string {
  return value.padStart(2, '0');
}

function toIsoDate(year: string, month: string, day: string): string | null {
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

export function parseStatementDate(token: string): string | null {
  const trimmed = token.trim();
  const dmy = DATE_DMY_DOT.exec(trimmed);
  if (dmy) return toIsoDate(dmy[3] ?? '', dmy[2] ?? '', dmy[1] ?? '');
  const iso = DATE_ISO.exec(trimmed);
  if (iso) return toIsoDate(iso[1] ?? '', iso[2] ?? '', iso[3] ?? '');
  const slash = DATE_DMY_SLASH.exec(trimmed);
  if (slash) return toIsoDate(slash[3] ?? '', slash[2] ?? '', slash[1] ?? '');
  return null;
}

/**
 * Integer cents for a statement amount token. Finnish grouping spaces and
 * mixed thousand/decimal separators are stripped before eurosToCents.
 * Returns a negative number for credit/minus lines; callers drop those.
 */
export function parseStatementAmountCents(raw: string): number | null {
  let token = raw.trim().replace(/\u00A0/g, ' ');
  if (!token) return null;
  const wrappedNegative = /^\(.*\)$/.test(token);
  token = token.replace(/[€]/g, '').trim();
  const signedNegative = token.startsWith('-') || token.startsWith('+');
  const negative = wrappedNegative || token.startsWith('-');
  if (wrappedNegative) {
    token = token.slice(1, -1).trim();
  }
  if (signedNegative) {
    token = token.slice(1).trim();
  }
  token = token.replace(/\s+/g, '');
  if (!token) return null;

  if (token.includes(',') && token.includes('.')) {
    const lastComma = token.lastIndexOf(',');
    const lastDot = token.lastIndexOf('.');
    token = lastComma > lastDot ? token.replace(/\./g, '') : token.replace(/,/g, '');
  } else if (/^\d{1,3}(\.\d{3})+$/.test(token)) {
    token = token.replace(/\./g, '');
  } else if (/^\d{1,3}(,\d{3})+$/.test(token)) {
    token = token.replace(/,/g, '');
  }

  const cents = eurosToCents(token);
  if (cents == null) return null;
  return negative ? -cents : cents;
}

function looksLikeAmount(token: string): boolean {
  const trimmed = token.trim();
  if (parseStatementDate(trimmed)) return false;
  if (/€/.test(trimmed)) return parseStatementAmountCents(trimmed) != null;
  const compact = trimmed.replace(/\u00A0/g, '').replace(/\s+/g, '');
  if (/^[+-]?\(.*\)$/.test(compact)) return parseStatementAmountCents(trimmed) != null;
  if (/^[+-]?\d{1,3}(\.\d{3})+(,\d{1,2})?$/.test(compact)) return true;
  if (/^[+-]?\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(compact)) return true;
  if (/^[+-]?\d+[.,]\d{1,2}$/.test(compact)) return true;
  return false;
}

function compactReference(token: string): string {
  return token.replace(/\s+/g, '').toUpperCase();
}

export function looksLikePaymentReference(token: string): boolean {
  const compact = compactReference(token);
  if (/^RF\d{2}[A-Z0-9]{4,21}$/.test(compact)) return true;
  const digits = compact.replace(/\D/g, '');
  if (digits.length < 4 || digits.length > 20) return false;
  return /^\d+$/.test(compact);
}

function splitLine(line: string): string[] {
  const trimmed = line.trim();
  if (!trimmed) return [];
  if (trimmed.includes('\t')) {
    return trimmed.split('\t').map((part) => part.trim()).filter((part) => part.length > 0);
  }
  if (trimmed.includes(';')) {
    return trimmed.split(';').map((part) => part.trim()).filter((part) => part.length > 0);
  }
  if (/\s{2,}/.test(trimmed)) {
    return trimmed.split(/\s{2,}/).map((part) => part.trim()).filter((part) => part.length > 0);
  }
  return trimmed.split(/\s+/).map((part) => part.trim()).filter((part) => part.length > 0);
}

function classifyTokens(tokens: string[]): ParsedStatementRow | null {
  let amountCents: number | null = null;
  let reference: string | null = null;
  let paymentDate: string | null = null;
  const nameParts: string[] = [];

  for (const token of tokens) {
    const date = parseStatementDate(token);
    if (date && paymentDate == null) {
      paymentDate = date;
      continue;
    }
    if (looksLikeAmount(token) && amountCents == null) {
      amountCents = parseStatementAmountCents(token);
      continue;
    }
    if (looksLikePaymentReference(token) && reference == null) {
      reference = compactReference(token);
      continue;
    }
    nameParts.push(token);
  }

  if (amountCents == null || amountCents <= 0) return null;

  const payerName = nameParts.join(' ').trim();
  const row: ParsedStatementRow = {
    amountCents,
    reference,
    payerName: payerName.length > 0 ? payerName : null,
    paymentDate,
  };
  return row;
}

/**
 * Parse a multi-line paste from online banking. Header rows, blank lines,
 * and credit/negative amounts are dropped.
 */
export function parseStatementText(text: string): ParsedStatementRow[] {
  const rows: ParsedStatementRow[] = [];
  for (const line of text.split(/\r?\n/)) {
    const tokens = splitLine(line);
    if (tokens.length === 0) continue;
    const parsed = classifyTokens(tokens);
    if (parsed) rows.push(parsed);
  }
  return rows;
}
